import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';
import { ENTRY_TYPES, formatDate, labelFor } from '@/lib/taxonomies';
import { ADMIN } from '@/constants/testIds';
import { useSeo } from '@/lib/seo';

export default function SeasonEntriesAdminPage() {
  useSeo({ title: 'Season Entries | Admin' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminApi.listSeason().then(setRows).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (id) => {
    try {
      await adminApi.deleteSeason(id);
      toast.success('Entry deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="kicker">Season Entries</p>
          <h1 className="font-serif text-3xl tracking-tight">The chronological journal.</h1>
        </div>
        <Link to="/admin/season/new">
          <Button data-testid={ADMIN.seasonCreate}>+ New Entry</Button>
        </Link>
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[46%]">Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10">
                    <p className="font-serif text-lg">No entries yet.</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Start with the first camera going up.</p>
                    <Link to="/admin/season/new" className="mt-4 inline-block">
                      <Button>+ First Entry</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r.id} data-testid={ADMIN.seasonRow}>
                  <TableCell className="font-medium">
                    <Link to={`/admin/season/${r.id}`} className="hover:underline">
                      {r.title}
                    </Link>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">/{r.slug}</p>
                  </TableCell>
                  <TableCell className="text-sm">{labelFor(ENTRY_TYPES, r.entry_type)}</TableCell>
                  <TableCell className="text-sm">{formatDate(r.date)}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === 'published' ? 'default' : 'secondary'}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right pr-6">
                    <div className="inline-flex gap-2">
                      <Link to={`/admin/season/${r.id}`}>
                        <Button size="sm" variant="secondary">Edit</Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost">Delete</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove “{r.title}” permanently. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(r.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
